// apps/office/src/pages/PartnerSettings.jsx
import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { User, Mail, Phone, Building2, Eye, LogOut, Edit, Save, X } from 'lucide-react';
import { toast } from 'sonner';
import pb from '../../lib/pocketbaseClient';

export default function PartnerSettings() {
  const { user, refreshUser, logout } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone?.toString() || '',
    address: user?.address || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const toggleEdit = () => {
    if (isEditing) {
      setFormData({
        name: user?.name || '',
        phone: user?.phone?.toString() || '',
        address: user?.address || '',
      });
    }
    setIsEditing(!isEditing);
  };

  const handleSave = async () => {
    if (!formData.name?.trim()) {
      toast.error('Name is required');
      return;
    }

    setLoading(true);
    try {
      const updates = {
        name: formData.name.trim(),
        phone: formData.phone?.trim() || null,
        address: formData.address?.trim() || null,
      };

      await pb.collection('users').update(user.id, updates);
      await refreshUser();
      toast.success('Profile updated successfully!');
      setIsEditing(false);
    } catch (err) {
      console.error('Update error:', err);
      toast.error('Failed to update profile: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <User className="w-6 h-6 text-amber-500" />
            Settings
          </h1>
          <p className="text-slate-500 text-sm">View and manage your account details</p>
        </div>
        <div className="text-xs text-slate-400 bg-amber-50 px-3 py-1 rounded-full border border-amber-200">
          ?? Secure
        </div>
      </div>

      <Card className="shadow-sm border-0">
        <CardContent className="p-6 space-y-6">
          {/* Avatar & Header */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
                <span className="text-2xl font-bold text-amber-700">
                  {user?.name?.charAt(0)?.toUpperCase() || 'P'}
                </span>
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-800">{user?.name || 'Partner'}</h2>
                <p className="text-sm text-slate-500 capitalize">{user?.role?.replace('-', ' ') || 'Partner'}</p>
              </div>
            </div>
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                className="text-amber-600 border-amber-200 hover:bg-amber-50"
                onClick={() => setIsEditing(true)}
              >
                <Edit className="w-4 h-4 mr-1" /> Edit Profile
              </Button>
            )}
          </div>

          {/* Read-only Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Mail className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span>{user?.email || 'N/A'}</span>
              <span className="text-xs text-slate-400 ml-auto">(read-only)</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Building2 className="w-4 h-4 text-slate-400 flex-shrink-0" />
              <span>{user?.role?.replace('-', ' ') || 'Partner'}</span>
              <span className="text-xs text-slate-400 ml-auto">(read-only)</span>
            </div>
          </div>

          {/* Editable Fields */}
          {isEditing ? (
            <div className="border-t border-slate-200 pt-4 space-y-4">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1">Full Name *</label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="mt-1"
                  placeholder="Your full name"
                />
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1">Phone Number</label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  className="mt-1"
                  placeholder="e.g. 9876543210"
                />
                <p className="text-xs text-slate-400 mt-1">Optional � enter a 10-digit number</p>
              </div>

              <div>
                <label htmlFor="address" className="block text-sm font-medium text-slate-700 mb-1">Address</label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="mt-1"
                  placeholder="Your office or home address"
                />
              </div>

              <div className="flex flex-wrap gap-3 pt-2">
                <Button
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={handleSave}
                  disabled={loading}
                >
                  {loading ? 'Saving...' : <><Save className="w-4 h-4 mr-1" /> Save Changes</>}
                </Button>
                <Button variant="outline" onClick={toggleEdit} disabled={loading}>
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="border-t border-slate-200 pt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-xs text-slate-500 font-medium">Phone</p>
                <p className="text-sm font-medium text-slate-800">{user?.phone || 'Not provided'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">Address</p>
                <p className="text-sm font-medium text-slate-800">{user?.address || 'Not provided'}</p>
              </div>
            </div>
          )}

          {!isEditing && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-2">
              <Eye className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-700">
                You are in <strong>view mode</strong>. Click <strong>Edit Profile</strong> to update your personal information.
              </p>
            </div>
          )}

          <Button
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50 w-full sm:w-auto"
            onClick={logout}
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}